/**
 * Gated Inline Annotations Component
 *
 * Tier-aware wrapper for InlineMessageDisplay that applies
 * blur teaser for premium features.
 */

import { useEffect, useCallback } from 'react';
import { View } from 'react-native';
import { useSDUIConfig, toUISpecTier } from '@dramadetector/ui/json-render';
import { GatedSection } from '@/components/gating';
import { useEntitlements } from '@/hooks/useEntitlements';
import { captureEvent } from '@/lib/analytics';
import { InlineMessageDisplay } from './InlineMessageDisplay';
import type { InlineMessageDisplayProps, RevisionTrackingConfig } from './InlineMessageDisplay';

export interface GatedInlineAnnotationsProps extends Omit<InlineMessageDisplayProps, 'tracking'> {
  /** Override tier for testing */
  overrideTier?: 'anonymous' | 'free' | 'drama_plus' | 'dramax' | 'family_member';
  /** Screen name for analytics */
  screen?: string;
  /** Callback when upgrade pressed */
  onUpgrade?: () => void;
  /** Optional revision tracking configuration (enables RLHF data collection) */
  tracking?: RevisionTrackingConfig;
  /** Callback when score changes due to accept/ignore actions (Score Chase live delta) */
  onScoreChange?: (
    revisedScore: number,
    scoreDelta: number,
    meta?: { acceptedCount: number; isRescoring: boolean; sessionComplete: boolean; lastAcceptedSeverity?: string },
  ) => void;
}

/**
 * Tier-based feature gating:
 * - L1 (Free/Anonymous): First N issues visible (SDUI-configurable), rest blurred
 * - L2 (Drama+): All issues visible
 * - L3 (DramaX): All issues + patterns + transformations
 */
const DEFAULT_ISSUE_LIMIT = 2;

/**
 * GatedInlineAnnotations - Tier-aware inline annotations
 */
export function GatedInlineAnnotations({
  message,
  issues,
  rewrites,
  overrideTier,
  screen = 'results',
  onUpgrade,
  tracking,
  onScoreChange,
  testID = 'gated-inline-annotations',
}: GatedInlineAnnotationsProps) {
  const { tier: detectedTier } = useEntitlements();
  const tier = overrideTier || detectedTier || 'free';

  // Defensive null safety for API response edge cases
  const safeIssues = issues || [];
  const safeRewrites = rewrites || [];

  // Determine premium access (family members inherit drama_plus/dramax tier from admin)
  const isPremium = tier === 'drama_plus' || tier === 'dramax';

  // SDUI config for issue count threshold
  const { config: sduiConfig } = useSDUIConfig('blur-teaser', toUISpecTier(tier));
  const issueLimit = sduiConfig.issueCountThreshold ?? DEFAULT_ISSUE_LIMIT;

  // For free users, only show first N issues (SDUI-configurable)
  const visibleIssues = isPremium ? safeIssues : safeIssues.slice(0, issueLimit);
  const gatedIssueCount = isPremium ? 0 : Math.max(0, safeIssues.length - issueLimit);

  // Track when gated content is shown (conversion funnel top)
  useEffect(() => {
    if (gatedIssueCount > 0) {
      captureEvent('Results_GatedContent_Shown', {
        content_type: 'inline_annotations',
        gated_count: gatedIssueCount,
        tier,
        screen,
      });
    }
  }, [gatedIssueCount, tier, screen]);

  // Wrap onUpgrade to track upgrade prompt clicks
  const handleUpgrade = useCallback(() => {
    captureEvent('Results_Upgrade_Prompt_Click', {
      content_type: 'inline_annotations',
      gated_count: gatedIssueCount,
      tier,
      screen,
    });
    onUpgrade?.();
  }, [gatedIssueCount, tier, screen, onUpgrade]);

  // If no gated content, render directly
  if (gatedIssueCount === 0) {
    return (
      <InlineMessageDisplay
        message={message}
        issues={visibleIssues}
        rewrites={safeRewrites}
        tier={tier}
        tracking={tracking}
        onScoreChange={onScoreChange}
        testID={testID}
      />
    );
  }

  // Render with gating for additional issues
  return (
    <View testID={testID}>
      {/* Always visible issues */}
      <InlineMessageDisplay
        message={message}
        issues={visibleIssues}
        rewrites={safeRewrites}
        tier={tier}
        tracking={tracking}
        onScoreChange={onScoreChange}
        testID={`${testID}-visible`}
      />

      {/* Gated additional issues teaser */}
      <GatedSection
        requiredTier="drama_plus"
        contentType="annotations"
        screen={screen}
        onUpgrade={handleUpgrade}
        gatedCount={gatedIssueCount}
        blurIntensity="heavy"
        testID={`${testID}-gated`}
      >
        {/* Preview of gated issues (blurred) */}
        <View style={{ opacity: 0.3 }}>
          <InlineMessageDisplay
            message={message}
            issues={safeIssues.slice(issueLimit)}
            rewrites={safeRewrites}
            tier={tier}
            testID={`${testID}-gated-content`}
          />
        </View>
      </GatedSection>
    </View>
  );
}

export default GatedInlineAnnotations;
